import React, { useState, useEffect, useCallback } from 'react';
import { IngredientCard } from 'components/customComponents/Layers/InputGroups';
import { ReactComponent as PlusCircle } from 'Icons/PlusCircle.svg';
import ELayersView from 'enums/ELayersView';
import { getIngredientName, getIngredientStock, isNullOrUndefined } from 'utils/Global';
import { LayersIngredientsProps } from 'business/models/CustomModels';
import { usePreserveExperiment, useRMDispatch, useRMSelector } from 'redux/hooks';
import RMButton from 'components/customComponents/Controls/RMButton';
import { getLinkedLayerGridDto } from 'business/plateviewer/GenerateUtil';
import { dirtifyOverLappingLayers } from 'business/plateviewer/layer/ResizeLayerTools';
import {
  selectIsEditable,
  selectIsModifyAccessRights,
} from 'redux/selectors/StateSelectors';
import { LayerDTO } from 'business/models/Models';
import { updateExperimentLayers } from 'redux/reducers/experimentDataReducer';
import {
  selectExperimentLayers,
  selectSelectedLayer,
  selectSelectedLayerId,
} from 'redux/selectors/ExperimentStateSelector';
import { cloneDeep } from 'lodash';
import { FmlxButton } from 'fmlx-common-ui';
import { useAllowLayerEditing } from 'redux/hooks/experiment';
import { useIsScreenIngredientEditable } from 'redux/hooks/common';

function GridLayerIngredients({
  setContentView,
  setEditIngredientData,
  setSelectedIngredient,
  setSelectedStocks,
}: LayersIngredientsProps) {
  const [gridLayerIng, setGridLayerIng] = useState([]);
  const [refresh, setRefresh] = useState<boolean>(false);
  const _selectedLayerId = useRMSelector(selectSelectedLayerId);
  const experimentLayers = useRMSelector(selectExperimentLayers);
  const dispatch = useRMDispatch();
  const isEditMode: boolean = useRMSelector(selectIsEditable);
  const modifyAccessRights: boolean = useRMSelector(selectIsModifyAccessRights);
  const selectedLayer = useRMSelector(selectSelectedLayer);
  const isLayerEditingAllowed = useAllowLayerEditing();
  const isScreenEditable = useIsScreenIngredientEditable();

  const setLayers = useCallback(
    (layers: LayerDTO[]) => dispatch(updateExperimentLayers(layers)),
    [dispatch],
  );
  const callPreserveUndoableState = usePreserveExperiment();

  function editGridLayerIngredients(gridIng) {
    const ingName = getIngredientName(gridIng.ingredientIngredientTypeId);
    setSelectedIngredient({
      id: gridIng.ingredientIngredientTypeId,
      name: ingName,
    });
    setEditIngredientData(gridIng);
    setSelectedStocks(getIngredientStock(gridIng.ingredientStockId));
    setContentView(ELayersView.EDITINGREDIENTSVIEW);
  }
  function deleteGridLayerIngredients(ingId) {
    callPreserveUndoableState();
    let layers = cloneDeep(experimentLayers);
    const selectedLayer = layers.find((lyr) => lyr.id === _selectedLayerId);
    const gridDto = getLinkedLayerGridDto(layers, selectedLayer.layerDetail);
    const deleteIndex = gridDto.gridIngredient.findIndex(
      (gridIng) => gridIng.id === ingId,
    );
    gridDto.gridIngredient.splice(deleteIndex, 1);
    setRefresh(!refresh);
    layers = layers.map((lyr) => dirtifyOverLappingLayers(lyr, selectedLayer, true));
    setLayers(layers);
  }

  useEffect(() => {
    if (!experimentLayers) {
      return;
    }
    const layers = [...experimentLayers];
    const selectedLayer = layers.find((lyr) => lyr.id === _selectedLayerId);
    const grid = getLinkedLayerGridDto(layers, selectedLayer.layerDetail);
    if (!isNullOrUndefined(grid)) {
      setGridLayerIng(grid.gridIngredient);
    }
  }, [_selectedLayerId, experimentLayers]);

  function getGridLayerIngView() {
    if (isNullOrUndefined(gridLayerIng)) {
      return null;
    }
    const ingredientsView = gridLayerIng.map((ing) => {
      return (
        <IngredientCard
          id={`commandpanel-gridlayering-card`}
          key={`gridLayerIng-${ing.id}`}
          ingredientData={ing}
          setSelectedIngredient={setSelectedIngredient}
          setEditIngredientData={setEditIngredientData}
          setContentView={setContentView}
          onEditIngredient={() => editGridLayerIngredients(ing)}
          onDeleteIngredient={() => deleteGridLayerIngredients(ing.id)}
          isEditMode={isEditMode && modifyAccessRights && isLayerEditingAllowed}
        />
      );
    });
    return ingredientsView;
  }

  function addIngredient() {
    setContentView(ELayersView.SELECTINGREDIENTSVIEW);
    setEditIngredientData({});
  }

  return (
    <>
      {isEditMode && modifyAccessRights && (
        <RMButton
          id="commandpanel-gridlayering-addingredient-button"
          disabled={selectedLayer?.locked || !isLayerEditingAllowed || !isScreenEditable}
          Icon={
            <PlusCircle
              id="commandpanel-gridlayering-addingredient-icon"
              style={{
                height: '1.5rem',
                width: '1.5rem',
                marginRight: '0.44rem',
              }}
            />
          }
          customStyle={{
            width: '14.81rem',
            height: '3rem',
            maxHeight: '3rem',
            minHeight: '3rem',
            borderRadius: '0.5rem',
            fontSize: '1.25rem',
            lineHeight: '1.45rem',
          }}
          withIcon={FmlxButton.WithIcon.START}
          onClick={addIngredient}
          label="Add Ingredient"
          type={FmlxButton.Type.POSITIVE}
        ></RMButton>
      )}
      <div
        id="commandpanel-gridlayering-ingredients-container"
        style={{ overflowY: 'auto', overflowX: 'hidden', height: '45vh', width: '100%' }}
      >
        {getGridLayerIngView()}
      </div>
    </>
  );
}

export default GridLayerIngredients;
